import { useState, useContext } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, XCircle, Heart } from "lucide-react";
import { resolveAssetUrl, productPath } from "../../config";
import { CartContext } from "../../context/CartContext";
import { toast } from "../Toast";
import { getDisplayPrices, shouldShowIncVatLabel } from "../../utils/pricing";

const fmt = (n) => (n != null ? "KES " + Number(n).toLocaleString() : "KES 0");

/** @deprecated use resolveAssetUrl from config */
export const getImageUrl = (path) => resolveAssetUrl(path);

function cardPricingFromProduct(p) {
  const salePrice = p.variant?.price?.sale_price ?? 0;
  return getDisplayPrices({
    price: p.variant?.price,
    salePrice,
    costPrice: p.variant?.price?.cost_price,
    lowestPrice: p.displayPrice ?? salePrice,
    displayPrice: p.displayPrice,
    offerPricing: p.offerPricing,
  });
}

const getBadges = (p) => {
  const badges = [];
  if (p.isBestSeller || p.badge === "Hot" || p.badge === "HOT") {
    badges.push({ label: "HOT", className: "bg-gradient-brand text-primary-foreground" });
  }
  if (p.isNewArrival || p.badge === "New" || p.badge === "NEW") {
    badges.push({ label: "NEW", className: "bg-gradient-brand text-primary-foreground" });
  }
  const pricing = cardPricingFromProduct(p);
  if (pricing.hasOffer) {
    badges.push({ label: "OFFER", className: "bg-destructive text-destructive-foreground" });
  } else {
    const salePrice = p.variant?.price?.sale_price;
    const costPrice = p.variant?.price?.cost_price;
    if (costPrice && salePrice && costPrice > salePrice) {
      badges.push({ label: "OFFER", className: "bg-destructive text-destructive-foreground" });
    }
  }
  if (p.badge && !["Hot", "HOT", "New", "NEW", "Sale"].includes(p.badge)) {
    badges.push({ label: p.badge.toUpperCase(), className: "bg-foreground/90 text-background" });
  } else if (p.badge === "Sale") {
    badges.push({ label: "OFFER", className: "bg-destructive text-destructive-foreground" });
  }
  return badges;
};

function resolveVariantId(p) {
  return p.variant?._id || p.availableVariantId || p.defaultVariantId || p.variantId;
}

function resolveInStock(p) {
  if (typeof p.inStock === "boolean") return p.inStock;
  if (typeof p.variant?.purchasable === "boolean") return p.variant.purchasable;
  if (typeof p.variant?.inStock === "boolean") return p.variant.inStock;
  if (p.variant?.forceOutOfStock || p.forceOutOfStock) return false;
  // Warehouse serial count (variant.stock / totalStock) is NOT storefront OOS
  // when STOREFRONT_ENFORCE_STOCK=false — only forceOutOfStock matters.
  return !!resolveVariantId(p);
}

function useHomeAddToCart(p) {
  const { addToCart } = useContext(CartContext);
  const [adding, setAdding] = useState(false);
  const variantId = resolveVariantId(p);
  const inStock = resolveInStock(p);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!inStock) return;
    if (!variantId) {
      toast.info("Open the product to choose a variant");
      return;
    }
    if (adding) return;

    try {
      setAdding(true);
      await addToCart(variantId, 1);
      toast.success(`${p.name} added to cart!`);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to add to cart";
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  return { handleAddToCart, adding, inStock, variantId };
}

/** Original homepage trending card (Shop the Hottest Drops) */
function LegacyTrendingCard({ p, marquee = false }) {
  const cheapestVariant = p.variant;
  const pricing = cardPricingFromProduct(p);
  const price = pricing.displayPrice;
  const strikePrice = pricing.strikePrice;
  const { handleAddToCart, adding, inStock } = useHomeAddToCart(p);
  const rawImage = p.image || cheapestVariant?.variantImage?.[0]?.path;
  const imageUrl = resolveAssetUrl(rawImage);
  const productLink = productPath(p.variantSlug || p.variant?.slug || p.slug);

  return (
    <div className={`group relative rounded-2xl bg-surface border border-border overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-card-hover flex flex-col flex-1 h-full ${marquee ? "rounded-xl" : ""}`}>
      <div className={`relative overflow-hidden bg-gradient-surface ${marquee ? "aspect-[5/4]" : "aspect-square"}`}>
        <Link
          to={productLink}
          className="flex h-full items-center justify-center p-4"
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={p.name}
              loading="lazy"
              className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
              No image
            </div>
          )}
          {p.badge && (
            <span
              className={`absolute top-3 left-3 px-2.5 py-1 text-[10px] font-bold tracking-wider rounded-md ${
                p.badge === "Sale"
                  ? "bg-destructive text-destructive-foreground"
                  : p.badge === "New"
                    ? "bg-gradient-brand text-primary-foreground"
                    : "bg-foreground/90 text-background"
              }`}
            >
              {p.badge.toUpperCase()}
            </span>
          )}
        </Link>
        <button
          type="button"
          aria-label="Add to cart"
          disabled={!inStock || adding}
          onClick={handleAddToCart}
          className="absolute bottom-0 left-0 right-0 py-3 bg-gradient-brand-strong text-primary-foreground text-sm font-semibold flex items-center justify-center gap-2 translate-y-full group-hover:translate-y-0 transition-transform duration-300 disabled:opacity-50 z-10"
        >
          <ShoppingCart className="h-4 w-4" />
          {adding ? "Adding..." : inStock ? "Add to Cart" : "Out of Stock"}
        </button>
      </div>
      <div className={`flex flex-col flex-grow justify-between ${marquee ? "p-3" : "p-4"}`}>
        <div>
          <Link to={productLink}>
            {p.brandId?.name ? (
              <p className="text-xs text-muted-foreground">{p.brandId.name}</p>
            ) : p.brand ? (
              <p className="text-xs text-muted-foreground">{p.brand}</p>
            ) : (
              <p className="text-xs text-transparent select-none">-</p>
            )}
            <h3 className="mt-0.5 font-semibold text-foreground truncate">{p.name}</h3>
          </Link>
          <div className="mt-1.5 flex flex-col gap-1">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <span className="text-primary/80">
                Available in {p.variantCount || 1} variants
              </span>
            </div>
            {!inStock && (
              <div className="flex items-center gap-1 text-[11px] font-semibold">
                <span className="flex items-center gap-1 text-destructive">
                  <XCircle className="h-3 w-3" /> Out of Stock
                </span>
              </div>
            )}
          </div>
        </div>
        <div className={`mt-3 flex items-baseline gap-2 flex-wrap ${marquee ? "mt-2" : ""}`}>
          <p className={`font-display font-bold text-gradient-brand ${marquee ? "text-base" : "text-lg"}`}>{fmt(price)}</p>
          {strikePrice && strikePrice > price && (
            <p className="text-sm text-muted-foreground line-through">{fmt(strikePrice)}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Theme-aware product card for CMS sections (matches legacy trending tokens) */
function CmsProductCard({ p, compact = false }) {
  const cheapestVariant = p.variant;
  const pricing = cardPricingFromProduct(p);
  const displayPrice = pricing.displayPrice;
  const strikePrice = pricing.strikePrice;
  const { handleAddToCart, adding, inStock } = useHomeAddToCart(p);
  const rawImage = p.image || cheapestVariant?.variantImage?.[0]?.path;
  const imageUrl = resolveAssetUrl(rawImage);
  const brandName = p.brandId?.name || p.brand || "";
  const badges = getBadges(p);
  const productLink = productPath(p.variantSlug || p.variant?.slug || p.slug);

  return (
    <div
      className={`group relative bg-surface border border-border overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-card-hover flex flex-col h-full ${
        compact ? "rounded-xl" : "rounded-2xl"
      }`}
    >
      <Link to={productLink} className="block flex-1">
        <div className="relative aspect-square overflow-hidden bg-gradient-surface flex items-center justify-center p-3 sm:p-4">
          {badges.map((b, i) => (
            <span
              key={b.label}
              className={`absolute left-2 ${i === 0 ? "top-2" : "top-8"} px-2 py-0.5 text-[10px] font-bold tracking-wider rounded-md ${b.className}`}
            >
              {b.label}
            </span>
          ))}
          <button
            type="button"
            aria-label="Add to wishlist"
            onClick={(e) => e.preventDefault()}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-surface/90 border border-border text-muted-foreground hover:text-destructive transition-colors"
          >
            <Heart className="h-4 w-4" />
          </button>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={p.name}
              loading="lazy"
              className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
              No image
            </div>
          )}
        </div>
        <div className={`${compact ? "p-3" : "p-4"} flex flex-col flex-grow`}>
          {brandName && (
            <p className="text-xs text-muted-foreground">{brandName}</p>
          )}
          <h3 className="mt-0.5 font-semibold text-foreground text-sm leading-tight line-clamp-2">
            {p.name}
          </h3>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <span className="font-display font-bold text-gradient-brand text-base">
              {fmt(displayPrice)}
            </span>
            {strikePrice && strikePrice > displayPrice && (
              <span className="text-xs text-muted-foreground line-through">
                {fmt(strikePrice)}
              </span>
            )}
          </div>
          {shouldShowIncVatLabel(p?.vatMode) && (
            <p className="text-[10px] text-muted-foreground mt-0.5">Inc. V.A.T</p>
          )}
        </div>
      </Link>
      <div className={`${compact ? "px-3 pb-3" : "px-4 pb-4"}`}>
        <button
          type="button"
          disabled={!inStock || adding}
          onClick={handleAddToCart}
          className={`block w-full text-center py-2 text-xs font-semibold uppercase tracking-wide transition-colors rounded-lg ${
            inStock
              ? "bg-gradient-brand-strong text-primary-foreground hover:opacity-90 disabled:opacity-60"
              : "bg-muted text-muted-foreground cursor-not-allowed"
          }`}
        >
          {adding ? "Adding..." : inStock ? "Add to Cart" : "Out of Stock"}
        </button>
      </div>
    </div>
  );
}

export function ProductCard({
  p,
  compact = false,
  variant = "cms",
  marquee = false,
}: {
  p: Record<string, unknown>;
  compact?: boolean;
  variant?: string;
  marquee?: boolean;
}) {
  if (variant === "legacy") {
    return <LegacyTrendingCard p={p} marquee={marquee} />;
  }
  return <CmsProductCard p={p} compact={compact} />;
}
